import { FirebaseError } from 'firebase/app';
import {
    type ActionCodeSettings,
    EmailAuthProvider,
    type User as FirebaseUser,
    createUserWithEmailAndPassword,
    confirmPasswordReset as firebaseConfirmPasswordReset,
    onIdTokenChanged,
    reauthenticateWithCredential,
    signInWithEmailAndPassword,
    updatePassword,
    updateProfile,
    multiFactor,
    PhoneAuthProvider,
    getMultiFactorResolver,
    MultiFactorResolver,
    MultiFactorError,
    RecaptchaVerifier,
    PhoneMultiFactorGenerator,
    PhoneInfoOptions,
    PhoneMultiFactorAssertion,
    PhoneMultiFactorInfo,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import nookies from 'nookies';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { auth as firebaseAuth } from '@config/firebase/client';
import { userAuth } from '@functions';
import { AuthEvent } from '@utils/consts/events';

export interface BaseAuthProps {
    emailVerified: boolean;
    multiFactor?: { enrolledFactors: PhoneMultiFactorInfo[] };
    uid: string;
}
export type SignupType = (email: string, password: string) => Promise<FirebaseUser | null>;

export type SigninResult =
    | { status: 'ERROR' }
    | { status: 'MFA_REQUIRED' }
    | { status: 'SUCCESS'; user: FirebaseUser };

export type SigninType = (
    email: string,
    password: string,
    recaptchaVerifier: RecaptchaVerifier,
) => Promise<SigninResult>;

export type MfaState = null | {
    resolver: MultiFactorResolver | null;
    verificationId: string | null;
};

export const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

export type AuthContextType = {
    changePassword: (newPassword: string) => Promise<boolean>;
    confirmPasswordReset: (password: string, resetCode: string) => Promise<boolean>;
    deleteUser: () => Promise<void>;
    errors: string | null;
    forceRefreshToken: () => Promise<string | undefined>;
    isLoading: boolean;
    logout: (redirect?: boolean) => Promise<void>;
    reauthenticateCurrentUser: (password: string) => Promise<boolean>;
    resetErrors: () => void;
    sendEmailVerification: (actionCodeSettings: ActionCodeSettings) => Promise<boolean>;
    sendPasswordResetEmail: (
        email: string,
        actionCodeSettings: ActionCodeSettings,
    ) => Promise<boolean>;
    sendSmsMfaSignIn: (
        resolver: MultiFactorResolver,
        hintIndex: number,
        verifier: RecaptchaVerifier,
    ) => Promise<string | null>;
    sendSmsMfaEnrollment: (
        user: FirebaseUser,
        phoneNumber: string,
        verifier: RecaptchaVerifier,
    ) => Promise<string | null>;
    mfaState: MfaState | null;
    setMfaState: (mfaState: MfaState | null) => void;
    assertSmsCodeMfaSignIn: (
        resolver: MultiFactorResolver,
        verificationId: string,
        code: string,
    ) => Promise<FirebaseUser | null>;
    assertSmsCodeMfaEnrollment: (
        user: FirebaseUser,
        verificationId: string,
        code: string,
    ) => Promise<boolean>;
    unenrollSmsMfa: (user: FirebaseUser, factorUid: string) => Promise<boolean>;
    signin: SigninType;
    signup: SignupType;
    updateDisplayName: (fullName: string) => void;
    user: BaseAuthProps | null;
    makeRecaptcha: () => RecaptchaVerifier;
};

export const AuthContext = createContext<AuthContextType>({
    assertSmsCodeMfaEnrollment: async () => false,
    assertSmsCodeMfaSignIn: async () => null,
    changePassword: async () => false,
    confirmPasswordReset: async () => false,
    deleteUser: async () => {},
    errors: null,
    forceRefreshToken: async () => undefined,
    isLoading: true,
    logout: async () => {},
    makeRecaptcha: () =>
        new RecaptchaVerifier(firebaseAuth, RECAPTCHA_CONTAINER_ID, { size: 'invisible' }),
    mfaState: null,
    reauthenticateCurrentUser: async () => false,
    resetErrors: () => {},
    sendEmailVerification: async () => false,
    sendPasswordResetEmail: async () => false,
    sendSmsMfaEnrollment: async () => null,
    sendSmsMfaSignIn: async () => null,
    setMfaState: () => {},
    signin: async () => ({ status: 'ERROR' }),
    signup: async () => null,
    unenrollSmsMfa: async () => false,
    updateDisplayName: () => {},
    user: null,
});

export const useAuth = (): AuthContextType => useContext(AuthContext);

type RunSigninOptions = {
    email: string;
    firebaseAuthInstance: typeof firebaseAuth;
    getMultiFactorResolverFn: typeof getMultiFactorResolver;
    password: string;
    recaptchaVerifier: RecaptchaVerifier;
    resetErrors: () => void;
    sendSmsMfaSignInFn: (
        resolver: MultiFactorResolver,
        hintIndex: number,
        verifier: RecaptchaVerifier,
    ) => Promise<string | null>;
    setAuthUser: (user: FirebaseUser | null) => void;
    setErrors: (error: string | null) => void;
    setMfaState: (mfaState: MfaState) => void;
    signInWithEmailAndPasswordFn: typeof signInWithEmailAndPassword;
};

export const runSignin = async ({
    email,
    firebaseAuthInstance,
    getMultiFactorResolverFn,
    password,
    recaptchaVerifier,
    resetErrors,
    sendSmsMfaSignInFn,
    setAuthUser,
    setErrors,
    setMfaState,
    signInWithEmailAndPasswordFn,
}: RunSigninOptions): Promise<SigninResult> => {
    try {
        resetErrors();
        setMfaState(null);
        const { user } = await signInWithEmailAndPasswordFn(firebaseAuthInstance, email, password);
        setAuthUser(user);

        return { status: 'SUCCESS', user };
    } catch (e) {
        if ((e as FirebaseError)?.code === 'auth/multi-factor-auth-required') {
            resetErrors();
            const resolver = getMultiFactorResolverFn(firebaseAuthInstance, e as MultiFactorError);
            const verificationId = await sendSmsMfaSignInFn(resolver, 0, recaptchaVerifier);

            if (verificationId) {
                setMfaState({
                    resolver,
                    verificationId,
                });

                return { status: 'MFA_REQUIRED' };
            }
        } else if (e instanceof FirebaseError) {
            setErrors(e.code);
        }

        return { status: 'ERROR' };
    }
};

const useProvideAuth = (initialAuth?: BaseAuthProps | null): AuthContextType => {
    const [authUser, setAuthUser] = useState<BaseAuthProps | null>(initialAuth ?? null);
    const [isLoading, setIsLoading] = useState(!initialAuth); // TODO: If not initialAuth, it is stuck in loading state
    const [mfaState, setMfaState] = useState<MfaState>(null);
    const [errors, setErrors] = useState<string | null>(null);
    const router = useRouter();

    const resetErrors = useCallback(() => setErrors(null), []);

    const makeRecaptcha = () =>
        new RecaptchaVerifier(firebaseAuth, RECAPTCHA_CONTAINER_ID, {
            // Callbacks below are needed as a fix of an issue in Firebase SDK v10 as described in https://github.com/firebase/firebase-js-sdk/issues/3356
            callback: () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)?.recaptchaVerifier?.recaptcha.reset();
            },
            'error-callback': () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)?.recaptchaVerifier?.recaptcha.reset();
            },
            'expired-callback': () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)?.recaptchaVerifier?.recaptcha.reset();
            },
            size: 'invisible',
        });

    const sendSms = (
        options: string | PhoneInfoOptions,
        verifier: RecaptchaVerifier,
    ): Promise<string> => new PhoneAuthProvider(firebaseAuth).verifyPhoneNumber(options, verifier);

    const makeAssertion = (verificationId: string, code: string): PhoneMultiFactorAssertion =>
        PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code));

    const deleteUser = async () => {
        await firebaseAuth.currentUser?.delete();
    };

    const sendSmsMfaEnrollment = async (
        user: FirebaseUser,
        phoneNumber: string,
        verifier: RecaptchaVerifier,
    ) => {
        try {
            const session = await multiFactor(user).getSession();
            const verificationId = await sendSms({ phoneNumber, session }, verifier);
            setMfaState({
                resolver: null,
                verificationId,
            });

            return verificationId;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return null;
        }
    };

    const assertSmsCodeMfaEnrollment = async (
        user: FirebaseUser,
        verificationId: string,
        code: string,
        displayName = '',
    ) => {
        try {
            const assertion = makeAssertion(verificationId, code);
            await multiFactor(user).enroll(assertion, displayName);

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const unenrollSmsMfa = async (user: FirebaseUser, factorUid: string) => {
        try {
            await multiFactor(user).unenroll(factorUid);

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const sendSmsMfaSignIn = async (
        resolver: MultiFactorResolver,
        hintIndex: number,
        verifier: RecaptchaVerifier,
    ) => {
        try {
            const hint = resolver.hints[hintIndex];
            const verificationId = await sendSms(
                { multiFactorHint: hint, session: resolver.session },
                verifier,
            );

            return verificationId;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return null;
        }
    };

    const assertSmsCodeMfaSignIn = async (
        resolver: MultiFactorResolver,
        verificationId: string,
        code: string,
    ): Promise<FirebaseUser | null> => {
        try {
            const assertion = makeAssertion(verificationId, code);
            const userCredential = await resolver.resolveSignIn(assertion);

            return userCredential.user;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return null;
        }
    };

    const signin: SigninType = async (email, password, recaptchaVerifier) => {
        setIsLoading(true);

        try {
            return await runSignin({
                email,
                firebaseAuthInstance: firebaseAuth,
                getMultiFactorResolverFn: getMultiFactorResolver,
                password,
                recaptchaVerifier,
                resetErrors,
                sendSmsMfaSignInFn: sendSmsMfaSignIn,
                setAuthUser,
                setErrors,
                setMfaState,
                signInWithEmailAndPasswordFn: signInWithEmailAndPassword,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const signup: SignupType = async (email, password) => {
        setIsLoading(true);
        try {
            resetErrors();
            const { user } = await createUserWithEmailAndPassword(firebaseAuth, email, password);
            setAuthUser(user);

            return user;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const updateDisplayName = async (fullName: string) => {
        try {
            if (firebaseAuth.currentUser) {
                await updateProfile(firebaseAuth.currentUser, { displayName: fullName });
            }
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }
        }
    };

    const logout = async (redirect = true) => {
        setIsLoading(true);
        try {
            resetErrors();
            await firebaseAuth.signOut();
            setAuthUser(null);

            if (redirect) {
                await router.push('/signin');
            }
            // Emit logout event specially for the BookPage to refresh the page (SSR)
            router.events.emit('routeChangeComplete', AuthEvent.LOGOUT);
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const sendPasswordResetEmail = async (
        email: string,
        actionCodeSettings: ActionCodeSettings,
    ) => {
        try {
            resetErrors();
            await userAuth({ action: 'sendPasswordResetLink', actionCodeSettings, email });

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const confirmPasswordReset = async (password: string, resetCode: string) => {
        try {
            await firebaseConfirmPasswordReset(firebaseAuth, resetCode, password);

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const reauthenticateCurrentUser = async (password: string) => {
        try {
            const credentials = EmailAuthProvider.credential(
                firebaseAuth?.currentUser?.email || '',
                password,
            );
            const { user } = firebaseAuth?.currentUser
                ? await reauthenticateWithCredential(firebaseAuth?.currentUser, credentials)
                : { user: null };
            setAuthUser(user);

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const sendEmailVerification = async (
        actionCodeSettings: ActionCodeSettings,
    ): Promise<boolean> => {
        try {
            const email = firebaseAuth.currentUser?.email || '';
            await userAuth({ action: 'sendEmailValidationLink', actionCodeSettings, email });

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const changePassword = async (newPassword: string) => {
        try {
            if (firebaseAuth.currentUser) {
                await updatePassword(firebaseAuth.currentUser, newPassword);
            }

            return true;
        } catch (e) {
            if (e instanceof FirebaseError) {
                setErrors(e.code);
            }

            return false;
        }
    };

    const forceRefreshToken = async () => firebaseAuth.currentUser?.getIdToken(true);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(firebaseAuth, async (user) => {
            const token = (await user?.getIdToken()) ?? '';
            nookies.set(undefined, 'token', token, { path: '/' });

            if (Boolean(user) !== Boolean(authUser)) {
                setAuthUser(user);
            }
            if (isLoading) {
                setIsLoading(false);
            }
        });

        const handleRouteChange = () => {
            resetErrors();
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            unsubscribe();
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [authUser, isLoading, resetErrors, router.events]);

    return {
        assertSmsCodeMfaEnrollment,
        assertSmsCodeMfaSignIn,
        changePassword,
        confirmPasswordReset,
        deleteUser,
        errors,
        forceRefreshToken,
        isLoading,
        logout,
        makeRecaptcha,
        mfaState,
        reauthenticateCurrentUser,
        resetErrors,
        sendEmailVerification,
        sendPasswordResetEmail,
        sendSmsMfaEnrollment,
        sendSmsMfaSignIn,
        setMfaState,
        signin,
        signup,
        unenrollSmsMfa,
        updateDisplayName,
        user: authUser,
    };
};

export const AuthProvider: FC<{
    children: ReactNode;
    initialAuth?: BaseAuthProps | null;
}> = ({ children, initialAuth }) => {
    const auth = useProvideAuth(initialAuth);

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
